import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getNotices, getLatestReleaseNote } from '../services/boardService';
import type { BoardPost } from '../types/board';
import { colors } from '../styles/colors';
import { IconClose } from './icons';

const DISMISSED_KEY = 'ourhertz_dismissed_notices';

function getDismissedIds(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]')); }
  catch { return new Set(); }
}

function dismissId(id: string) {
  const set = getDismissedIds();
  set.add(id);
  localStorage.setItem(DISMISSED_KEY, JSON.stringify([...set].slice(-50)));
}

/** Clear all dismissed notices (called from settings) */
export function clearDismissedNotices() {
  localStorage.removeItem(DISMISSED_KEY);
}

export default function NoticePopup() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const lang = isKo ? 'ko' : 'en';

  const [queue, setQueue] = useState<BoardPost[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Only show once per browser session (new tab / refresh / login)
    if (sessionStorage.getItem('ourhertz_notice_shown')) { setLoaded(true); return; }
    sessionStorage.setItem('ourhertz_notice_shown', '1');

    (async () => {
      const dismissed = getDismissedIds();
      const posts: BoardPost[] = [];

      // Notices first, then release note
      const notices = await getNotices(lang).catch(() => []);
      for (const n of notices) {
        if (!dismissed.has(n.id)) posts.push(n);
      }

      const rn = await getLatestReleaseNote(lang).catch(() => null);
      if (rn && !dismissed.has(rn.id) && !posts.find(p => p.id === rn.id)) {
        posts.push(rn);
      }

      setQueue(posts);
      setLoaded(true);
    })();
  }, [lang]);

  if (!loaded || queue.length === 0 || currentIdx >= queue.length) return null;

  const post = queue[currentIdx];

  const handleClose = () => {
    if (dontShowAgain) dismissId(post.id);
    setDontShowAgain(false);
    // Move to next post
    setCurrentIdx(i => i + 1);
  };

  const formatDate = (ts: any) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl"
        style={{ backgroundColor: colors.surface, border: `2px solid ${post.isReleaseNote ? colors.warning : colors.error}` }}
        onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between px-4 py-3"
          style={{ backgroundColor: post.isReleaseNote ? `${colors.warning}15` : `${colors.error}15`, borderBottom: `1px solid ${colors.border}` }}>
          <div className="flex items-center gap-2">
            <span className="text-base">{post.isReleaseNote ? '📋' : '📢'}</span>
            <div>
              <span className="text-xs font-bold" style={{ color: post.isReleaseNote ? colors.warning : colors.error }}>
                {post.isReleaseNote ? (isKo ? '릴리즈 노트' : 'Release Note') : (isKo ? '공지사항' : 'Notice')}
              </span>
              <p className="text-[10px]" style={{ color: colors.textMuted }}>{formatDate(post.createdAt)}</p>
            </div>
          </div>
          <button onClick={handleClose}><IconClose size={18} color={colors.textMuted} /></button>
        </div>

        <div className="px-4 pt-3 pb-1">
          <h2 className="text-base font-bold" style={{ color: colors.text }}>{post.title}</h2>
          <p className="text-[10px]" style={{ color: colors.textMuted }}>{(post as any).author || post.authorNickname}</p>
        </div>

        <div className="max-h-60 overflow-y-auto px-4 py-2">
          <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: colors.text }}>{post.content}</p>
        </div>

        <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: `1px solid ${colors.border}` }}>
          <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: colors.textMuted }}>
            <input type="checkbox" checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} className="accent-[#4CAF50]" />
            {isKo ? '다시 보지 않기' : "Don't show again"}
          </label>
          <div className="flex items-center gap-2">
            {currentIdx < queue.length - 1 && (
              <span className="text-[10px]" style={{ color: colors.textMuted }}>
                {currentIdx + 1}/{queue.length}
              </span>
            )}
            <button onClick={handleClose} className="rounded-lg px-4 py-2 text-sm font-bold text-white" style={{ backgroundColor: colors.primary }}>
              {isKo ? '확인' : 'OK'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
